import api from './api';

export interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  phone?: string;
  perfil?: string;
  perfilId?: string;
  status: string;
  createdAt: Date | string;
  lastLogin?: Date | string;
}

export interface UserStats {
  total: number;
  ativos: number;
  inativos: number;
  admins: number;
  pendingDevelopers?: number;
}

export interface UserFilters {
  search?: string;
  perfil?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  stats?: UserStats;
}

class UsersService {
  /**
   * Get all users with filters
   */
  async getUsers(filters?: UserFilters): Promise<UsersResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.perfil) params.append('perfil', filters.perfil);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const response = await api.get(`/admin/users?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<User> {
    try {
      const response = await api.get(`/admin/users/${id}`);
      return response.data.data;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  }

  /**
   * Create new user
   */
  async createUser(data: Partial<User>): Promise<User> {
    try {
      const response = await api.post('/admin/users', data);
      return response.data.data;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  /**
   * Update user
   */
  async updateUser(id: string, data: Partial<User>): Promise<User> {
    try {
      const response = await api.put(`/admin/users/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    try {
      await api.delete(`/admin/users/${id}`);
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  /**
   * Activate user
   */
  async activateUser(id: string): Promise<User> {
    try {
      const response = await api.post(`/admin/users/${id}/activate`);
      return response.data.data;
    } catch (error) {
      console.error('Activate user error:', error);
      throw error;
    }
  }

  /**
   * Deactivate user
   */
  async deactivateUser(id: string): Promise<User> {
    try {
      const response = await api.post(`/admin/users/${id}/deactivate`);
      return response.data.data;
    } catch (error) {
      console.error('Deactivate user error:', error);
      throw error;
    }
  }

  /**
   * Approve pending developer account
   */
  async approveUser(id: string): Promise<void> {
    try {
      await api.post(`/admin/users/${id}/approve`);
    } catch (error) {
      console.error('Approve user error:', error);
      throw error;
    }
  }
}

export default new UsersService();
